import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Truck, Globe, HeadphonesIcon, Award } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/87d05315-7468-4327-bf5b-c097536c3594/images/c8845595f23861bc689a08966519feb2514761abbd446c0f3f7233905008bf73.png";

const Home = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // First try to seed data
        await axios.post(`${API}/seed`).catch(() => {});
        
        // Then fetch categories
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const features = [
    { icon: Truck, title: "Local Delivery Available", desc: "Fast local delivery in Florida" },
    { icon: Globe, title: "Shipping USA & Bahamas", desc: "We ship to all locations" },
    { icon: HeadphonesIcon, title: "Personalized Support", desc: "Dedicated customer service" },
    { icon: Award, title: "Quality Guaranteed", desc: "Premium print quality" },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="hero-section bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        data-testid="hero-section"
      >
        <div className="hero-content text-center px-4 max-w-4xl mx-auto">
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-light text-white mb-6 animate-fade-in">
            Premium <span className="gold-gradient font-semibold">Print</span> Services
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-2xl mx-auto animate-fade-in" style={{ animationDelay: "0.2s" }}>
            Make your brand stand out with quality print services from Velvet Press. 
            Serving The Bahamas and United States.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{ animationDelay: "0.4s" }}>
            <Link 
              to="/shop" 
              className="btn-gold inline-flex items-center justify-center"
              data-testid="shop-now-btn"
            >
              Shop Now
              <ArrowRight className="ml-2" size={18} />
            </Link>
            <Link 
              to="/request-quote" 
              className="btn-outline-gold inline-flex items-center justify-center"
              data-testid="request-quote-btn"
            >
              Request a Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Features Bar */}
      <section className="bg-[#141414] py-8 border-y border-white/10" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center space-x-3">
                <feature.icon className="text-[#D4AF37] flex-shrink-0" size={24} />
                <div>
                  <h4 className="font-outfit text-white text-sm font-medium">{feature.title}</h4>
                  <p className="text-gray-500 text-xs">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="categories-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-outfit text-3xl sm:text-4xl text-white mb-4">
              Our <span className="gold-gradient">Services</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              From business cards to large format printing, we offer a comprehensive range of print services
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center">
              <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 animate-stagger">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/shop/${category.slug}`}
                  className="category-card group relative rounded-sm overflow-hidden border border-white/10 card-hover"
                  data-testid={`category-${category.slug}`}
                >
                  <img
                    src={category.image || "https://images.unsplash.com/photo-1633415565464-e1d69c3fd906?w=600"}
                    alt={category.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent z-10"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                    <h3 className="font-outfit text-xl text-white group-hover:text-[#D4AF37] transition-colors">
                      {category.name}
                    </h3>
                    <span className="text-[#D4AF37] text-sm flex items-center mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      View Products <ArrowRight size={14} className="ml-1" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#141414]" data-testid="process-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-outfit text-3xl sm:text-4xl text-white mb-4">
              Simple <span className="gold-gradient">3-Step</span> Process
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              We make your print experience seamless from start to finish
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Define", desc: "Share your project requirements and vision with our team" },
              { step: "02", title: "Design", desc: "Our experienced designers create mockups for your approval" },
              { step: "03", title: "Print", desc: "Once approved, we print with precision and deliver on time" },
            ].map((item, index) => (
              <div 
                key={index} 
                className="text-center p-8 bg-[#0A0A0A] border border-white/10 rounded-sm card-hover"
                data-testid={`process-step-${index + 1}`}
              >
                <span className="font-outfit text-5xl text-[#D4AF37]/20">{item.step}</span>
                <h3 className="font-outfit text-2xl text-white mt-4 mb-3">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-outfit text-3xl sm:text-4xl text-white mb-6">
            Ready to <span className="gold-gradient">Get Started?</span>
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Let us help you create designs that will make a lasting impression. 
            Contact us today for a custom quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/request-quote" 
              className="btn-gold inline-flex items-center justify-center"
              data-testid="cta-quote-btn"
            >
              Request a Quote
              <ArrowRight className="ml-2" size={18} />
            </Link>
            <Link 
              to="/contact" 
              className="btn-outline-gold inline-flex items-center justify-center"
              data-testid="cta-contact-btn"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
